// Sign-in page (ADMIN_UI §5.1). GIS-direct: load gsi/client, initialise with
// our client_id + hosted-domain hint, render the official "Continue with
// Google" button. On a successful credential we decode + validate
// client-side (UX guard; the api re-verifies), then navigate to ?return or
// /dashboard. WrongDomainError → wrong-domain toast.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuthStore, WrongDomainError } from '../lib/auth-store.js';
import { GOOGLE_CLIENT_ID, HOSTED_DOMAIN } from '../lib/config.js';
import { getGis, loadGis, promptSilent } from '../lib/gis.js';

export const Route = createFileRoute('/sign-in')({
  validateSearch: (s: Record<string, unknown>) => ({
    return: typeof s.return === 'string' ? s.return : '/dashboard',
  }),
  component: SignInPage,
});

function SignInPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const signIn = useAuthStore((s) => s.signIn);
  const setRefreshFn = useAuthStore((s) => s.setRefreshFn);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Sign-in is misconfigured: VITE_GOOGLE_CLIENT_ID is not set.');
      return;
    }
    let cancelled = false;

    function handleCredential(credential: string) {
      try {
        signIn(credential);
      } catch (err) {
        if (err instanceof WrongDomainError) {
          toast.error(`This account is not in the ${HOSTED_DOMAIN} domain.`);
        } else {
          toast.error(err instanceof Error ? err.message : 'Sign-in failed');
        }
        return;
      }
      void navigate({ to: search.return });
    }

    loadGis()
      .then(() => {
        if (cancelled) return;
        const gis = getGis();
        if (!gis) return;
        gis.initialize({
          client_id: GOOGLE_CLIENT_ID,
          hd: HOSTED_DOMAIN,
          // Silent re-auth on page refresh: with auto_select enabled, GIS
          // will re-issue an ID token via the callback without UI when the
          // user previously consented and the Google session is still alive
          // (ADMIN_UI §5.5). Combined with the prompt() call below this means
          // a refresh lands on /sign-in for a beat and bounces straight to
          // /dashboard — no click required.
          auto_select: true,
          // Legacy (non-FedCM) prompt flow. FedCM's gsi/status check can 403
          // depending on Chrome 3P-cookie / FedCM-API state even when the JS
          // origins are correctly configured; the legacy flow is more
          // forgiving and works the same way on the SPA side.
          use_fedcm_for_prompt: false,
          callback: (r) => handleCredential(r.credential),
        });
        // Wire silent-refresh for the api-client's 401 path.
        setRefreshFn(() => promptSilent());
        if (buttonRef.current) {
          gis.renderButton(buttonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            width: 280,
          });
        }
        // Kick off One Tap / silent re-auth — fires the callback above if
        // the user is already signed in to Google. Cheap no-op if not.
        gis.prompt();
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load Google Sign-In');
      });

    return () => {
      cancelled = true;
    };
    // Effect runs once on mount — GIS only needs initialising once.
  }, []);

  return (
    <main className="grid min-h-screen place-items-center bg-neutral-50">
      <div className="w-full max-w-sm rounded-lg border bg-white p-8 shadow-sm">
        <h1 className="text-xl font-semibold">Email Retention Admin</h1>
        <p className="mt-2 text-sm text-neutral-600">
          Sign in with your {HOSTED_DOMAIN} Google account.
        </p>

        <div ref={buttonRef} className="mt-6 flex min-h-11 justify-center" />

        {error && (
          <p role="alert" className="mt-4 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    </main>
  );
}
